import { JSX } from "react";
import { IbssComponent } from "../../../../Components/Core/BaseComponent/IbssComponent";
import { appContext } from "../../../../AppContext";
import { Button, ButtonBar, Card, DeleteButton, Dialog, DialogContent, DialogFooter, GridContainer, GridItem, LeftButtons, MultilingualTextField, RightButtons, TextField } from "@iconics/components";
import { SerendieSymbol } from "@serendie/symbols";
import { ProgressIndicatorIndeterminate, Switch } from "@serendie/ui";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { PagedResponse, TypeHelper, Validator } from "@iconics/core";
import OntologyNodesCard, { IOntologyNode } from "../Edit/OntologyNodesCard";
import OntologyNodeParametersCard from "../Edit/OntologyNodeParametersCard";
import DataPointsCard from "../Edit/DataPointsCard";
import { INode2OntologyNodeData, INodeData, IPatchNodeResponse } from "./DataModels";
import "./Styles.scss";

export default class NodeDetailsSidePanel extends IbssComponent<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private languageValuesHelper: LanguageValuesHelper;
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private userCanUpdate: boolean;
    private userCanDelete: boolean;
    // Bumped on every loadNode call (and on selection clear) so that in-flight
    // requests for previously-selected nodes can detect they are stale and discard
    // their results instead of stomping over the current selection's data.
    private loadSequence = 0;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);
        this.state = {
            name: '',
            nameError: '',
            displayName: this.languageValuesHelper.default(),
            isEnabled: false,
            nodeId: 0,
            ontologyNodes: [],
            concurrencyStamp: '',
            isLoading: false,
            showParametersDialog: false,
            showDataPointsDialog: false,
        };
        this.userCanUpdate = this.local.hasRight('DATAMODEL.Nodes.Update');
        this.userCanDelete = this.local.hasRight('DATAMODEL.Nodes.Delete');
    }

    public componentDidMount(): void
    {
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();

        if (this.props.nodeId)
        {
            this.loadNode(this.props.nodeId);
        }
    }

    public componentDidUpdate(prevProps: Readonly<IProps>): void
    {
        if (prevProps.nodeId === this.props.nodeId)
        {
            return;
        }

        if (this.props.nodeId)
        {
            this.loadNode(this.props.nodeId);
        }
        else
        {
            // Bump the sequence so any in-flight load from the previous selection
            // sees itself as stale and exits without overwriting this cleared state.
            this.loadSequence++;
            this.setState({
                name: '',
                nameError: '',
                displayName: this.languageValuesHelper.default(),
                isEnabled: false,
                nodeId: 0,
                ontologyNodes: [],
                concurrencyStamp: '',
                isLoading: false,
            });
        }
    }

    private async loadingScoped(action: () => Promise<void>): Promise<void>
    {
        // Keep the spinner local to this side panel rather than triggering the global
        // app-level loader (this.loading()) — the user can still interact with the rest
        // of the page while node details are being fetched or saved.
        await this.setStateAsync({ isLoading: true });
        try
        {
            await action();
        }
        finally
        {
            await this.setStateAsync({ isLoading: false });
        }
    }

    private async loadNode(nodeId: number): Promise<void>
    {
        // Tag this invocation. Any later loadNode (or selection clear) bumps the
        // shared counter past `sequence`, which lets us bail out at every await
        // boundary if the user has moved on to a different node mid-flight.
        const sequence = ++this.loadSequence;
        await this.setStateAsync({ isLoading: true });

        try
        {
            const nodeData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.get<INodeData>({
                nodeId: nodeId,
                tenancy: appContext().tenantId,
            });
            if (this.loadSequence !== sequence) return;

            // Pre-fetch the node's ontology nodes so the Parameters dialog works even
            // if the user opens it before the Ontology Nodes dialog. The OntologyNodes
            // dialog itself also fires onOntologyNodesLoaded after its own fetch, which
            // keeps this list in sync if the user adds or removes nodes there.
            const ontologyNodeData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.ontologyNodes.get<PagedResponse<INode2OntologyNodeData[]>>({
                tenancy: appContext().tenantId,
                nodeId: nodeId,
            });
            if (this.loadSequence !== sequence) return;

            const ontologyNodes: IOntologyNode[] = ontologyNodeData.value.map(x => ({
                id: x.OntologyNode_ID,
                name: x.OntologyNode_Name,
                ontologyName: x.Ontology_Name,
                ontologyNodeName: x.OntologyNode_Name
            }));

            await this.setStateAsync({
                name: nodeData.Name,
                nameError: '',
                displayName: this.languageValuesHelper.fromString(nodeData.DisplayName),
                isEnabled: TypeHelper.toBoolean(nodeData.IsEnabled) ?? false,
                nodeId: nodeData.Id,
                ontologyNodes: ontologyNodes,
                concurrencyStamp: nodeData["@odata.etag"],
            });
        }
        finally
        {
            // Only the latest loader is allowed to flip the spinner off — older
            // invocations would clear it while a newer load is still in flight.
            if (this.loadSequence === sequence)
            {
                await this.setStateAsync({ isLoading: false });
            }
        }
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }

        await this.loadingScoped(async () =>
        {
            const response = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.patch<IPatchNodeResponse>({
                tenancy: appContext().tenantId,
                nodeId: this.state.nodeId,
                body: {
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    IsEnabled: this.state.isEnabled ? 1 : 0,
                    "@odata.etag": this.state.concurrencyStamp,
                },
            });

            this.setState({ concurrencyStamp: response.CreatedObject["@odata.etag"] });

            this.fireOnNodeChanged(this.state.ontologyNodes);
        });
    }

    private handleDelete(): Promise<void>
    {
        return this.props.onDelete(this.state.nodeId);
    }

    private fireOnNodeChanged(ontologyNodes: IOntologyNode[]): void
    {
        this.props.onNodeChanged({
            nodeId: this.state.nodeId,
            name: this.state.name,
            displayName: this.state.displayName,
            isEnabled: this.state.isEnabled,
            ontologyNodeIds: ontologyNodes.map(n => n.id),
        });
    }

    private handleOntologyNodesLoaded(ontologyNodes: IOntologyNode[]): void
    {
        // The inline OntologyNodesCard fires this on its initial fetch AND after
        // add/remove operations. We only want to notify the parent when the set
        // actually changed — and only once the side panel's state has caught up
        // to the prop selection (otherwise the freshly-fetched list belongs to
        // a node we haven't loaded yet, and would be reported against the wrong id).
        const prevKey = this.state.ontologyNodes.map(n => n.id).sort().join('|');
        const nextKey = ontologyNodes.map(n => n.id).sort().join('|');
        const setChanged = prevKey !== nextKey;

        this.setState({ ontologyNodes });

        if (setChanged && this.state.nodeId !== 0 && this.state.nodeId === this.props.nodeId)
        {
            this.fireOnNodeChanged(ontologyNodes);
        }
    }
    
    public render(): JSX.Element
    {
        const labels = this.labels;

        return (
            <div className="node-details-side-panel">
                {(this.state.isLoading || (!this.props.graphRefreshToken && this.props.nodeId)) && (
                    <div className="node-details-side-panel__loading">
                        <ProgressIndicatorIndeterminate
                            type="circular"
                            size="large"
                            color="subtle"
                        />
                        <span>{this.labels.Platform_Graph_Loading}</span>
                    </div>
                )}
                <div className="node-details-side-panel__content">
                    { !this.state.isLoading && this.state.nodeId === 0 &&
                        <div className="node-details-side-panel__empty">
                            <SerendieSymbol name="information" size={48} />
                            <span className="node-details-side-panel__empty-text">
                                {this.labels.Platform_AssetWorX_NoElementSelected}
                            </span>
                        </div>
                    }
                    { (this.state.isLoading || this.state.nodeId !== 0) &&
                    <GridContainer spacing="cards">
                        <GridItem>
                            <Card
                                title={labels.Platform_AssetWorX_NodeDetailsTitle}
                                subTitle={labels.Platform_AssetWorX_NodeDetailsSubTitle}
                                icon={<SerendieSymbol name="layer" />}
                            >
                                <GridContainer>
                                    <GridItem>
                                        <TextField
                                            required
                                            label={labels.Node_Name}
                                            placeholder={labels.Node_Name}
                                            value={this.state.name}
                                            onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                                            invalid={!!this.state.nameError}
                                            invalidMessage={this.state.nameError}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <MultilingualTextField
                                            label={labels.Node_DisplayName}
                                            placeholder={labels.Node_DisplayName}
                                            value={this.state.displayName}
                                            onChange={value => this.setState({ displayName: value })}
                                            localisation={appContext().localisation}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <Switch
                                            label={labels.Node_IsEnabled}
                                            checked={this.state.isEnabled}
                                            onCheckedChange={e => this.setState({ isEnabled: e.checked as boolean })}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <TextField
                                            disabled
                                            label={labels.Node_Id}
                                            placeholder={labels.Node_Id}
                                            value={this.state.nodeId}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <OntologyNodesCard
                                            nodeId={this.state.nodeId}
                                            onOntologyNodesLoaded={i => this.handleOntologyNodesLoaded(i)}
                                            onSectionUpdate={() => { }}
                                            size="small"
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <Button
                                            styleType='outlined'
                                            leftIcon={<SerendieSymbol name='gear' />}
                                            onClick={() => this.setState({ showParametersDialog: true })}
                                        >
                                            {labels.Platform_Node_MetaDataListTitle}
                                        </Button>
                                    </GridItem>
                                    <GridItem>
                                        <Button
                                            styleType='outlined'
                                            leftIcon={<SerendieSymbol name='activity' />}
                                            onClick={() => this.setState({ showDataPointsDialog: true })}
                                        >
                                            {labels.Platform_Node_DataPointListTitle}
                                        </Button>
                                    </GridItem>
                                </GridContainer>
                            </Card>
                        </GridItem>
                    </GridContainer>
                    }
                </div>
                { (this.state.isLoading || this.state.nodeId !== 0) &&
                <div className="node-details-side-panel__footer">
                    <ButtonBar>
                        <LeftButtons>
                            <DeleteButton
                                disabled={!this.userCanDelete}
                                dialogMessage={labels.Platform_Node_ConfirmDeleteMsg}
                                onDelete={() => this.handleDelete()}
                            />
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='filled'
                                disabled={!this.userCanUpdate || !this.validator.isValid}
                                onClick={() => this.handleSave()}
                            >
                                {labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </div>
                }

                { this.state.nodeId !== 0 && (
                    <Dialog
                        isOpen={this.state.showParametersDialog}
                        onOpenChange={() => this.setState({ showParametersDialog: false })}
                    >
                        <DialogContent>
                            <OntologyNodeParametersCard
                                nodeId={this.state.nodeId}
                                ontologyNodes={this.state.ontologyNodes}
                                onSectionUpdate={() => { }}
                            />
                        </DialogContent>
                        <DialogFooter>
                            <ButtonBar>
                                <RightButtons>
                                    <Button
                                        styleType='outlined'
                                        onClick={() => this.setState({ showParametersDialog: false })}
                                    >
                                        {labels.Platform_CommonAction_Cancel}
                                    </Button>
                                </RightButtons>
                            </ButtonBar>
                        </DialogFooter>
                    </Dialog>
                )}

                { this.state.nodeId !== 0 && (
                    <Dialog
                        isOpen={this.state.showDataPointsDialog}
                        onOpenChange={() => this.setState({ showDataPointsDialog: false })}
                    >
                        <DialogContent>
                            <DataPointsCard
                                nodeId={this.state.nodeId}
                                onAdd={() => this.props.onNavigateToDataPoint(this.state.nodeId, undefined)}
                                onEdit={i => this.props.onNavigateToDataPoint(this.state.nodeId, i)}
                                onSectionUpdate={() => { }}
                            />
                        </DialogContent>
                        <DialogFooter>
                            <ButtonBar>
                                <RightButtons>
                                    <Button
                                        styleType='outlined'
                                        onClick={() => this.setState({ showDataPointsDialog: false })}
                                    >
                                        {labels.Platform_CommonAction_Cancel}
                                    </Button>
                                </RightButtons>
                            </ButtonBar>
                        </DialogFooter>
                    </Dialog>
                )}

            </div>
        );
    }
}

export interface INodeChangeDetails
{
    nodeId: number;
    name: string;
    displayName: ILanguageValues;
    isEnabled: boolean;
    // Just the IDs — NodesGraphView keeps a parent-level cache of ontology nodes
    // (name + styling) and looks them up by id. If any id here isn't in the cache
    // yet, the parent fetches and inserts it before re-rendering.
    ontologyNodeIds: string[];
}

export interface IProps
{
    nodeId?: number;
    // Empty string while the parent's graph is still loading. When this and nodeId
    // are both set we render the same "refreshing" overlay the graph uses, so the
    // side panel doesn't show stale data for a node that may have just been deleted.
    graphRefreshToken: string;
    // Fires when the user saves the form OR when the ontology-node list changes
    // (add/remove from the inline OntologyNodesCard). Payload reflects the panel's
    // current state — the parent should use it to rehydrate just this one node in
    // the graph rather than refetching the whole graph.
    onNodeChanged: (details: INodeChangeDetails) => void;
    onDelete: (nodeId: number) => Promise<void>;
    onNavigateToDataPoint: (nodeId: number, dataPointId: string | undefined) => void;
}

export interface IState
{
    name: string;
    nameError: string;
    displayName: ILanguageValues;
    isEnabled: boolean;
    nodeId: number;
    ontologyNodes: IOntologyNode[];
    concurrencyStamp: string;
    isLoading: boolean;
    showParametersDialog: boolean;
    showDataPointsDialog: boolean;
}
